import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { ProjectStage, SourcingProjectCandidateView } from "@shared/labs/sourcing";
import { GripVertical, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";
import { shortLocation } from "../lib";

const STAGES: { stage: ProjectStage; title: string }[] = [
  { stage: "sourced", title: "Sourced" },
  { stage: "shortlisted", title: "Shortlisted" },
  { stage: "rejected", title: "Rejected" },
];

/**
 * Collision board — shown when an add brings candidates ALREADY in the project.
 * The colliding candidates appear at the stage they currently sit in; the
 * recruiter can drag them between the three lists to re-triage. New (non-
 * colliding) candidates were imported silently into Sourced and don't appear
 * here. Every move persists immediately via `sourcingProjectSetStage`.
 */
export function CollisionBoard({
  open,
  onOpenChange,
  projectId,
  rows,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** The colliding project rows (resolved from the add's `skippedCandidateIds`). */
  rows: SourcingProjectCandidateView[];
  /** Fired on close so the page reconciles with the persisted stages. */
  onChanged: () => void;
}) {
  const { toast } = useToast();
  // rowId → current column. Seeded from each row's persisted stage on open;
  // drags mutate this optimistically and the RPC persists in the background.
  const [placement, setPlacement] = useState<Record<string, ProjectStage>>({});

  useEffect(() => {
    if (open) setPlacement(Object.fromEntries(rows.map((r) => [r.id, r.stage])));
  }, [open, rows]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  async function onDragEnd(e: DragEndEvent) {
    const rowId = String(e.active.id);
    const target = e.over?.id as ProjectStage | undefined;
    if (!target) return;
    const current = placement[rowId];
    if (!current || current === target) return;

    setPlacement((prev) => ({ ...prev, [rowId]: target }));
    try {
      await rpcClient.labs.sourcingProjectSetStage({
        projectId,
        candidateRowId: rowId,
        stage: target,
      });
    } catch (err) {
      setPlacement((prev) => ({ ...prev, [rowId]: current })); // revert
      toast({
        title: "Falha ao mover candidato",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    }
  }

  function done() {
    onOpenChange(false);
    onChanged();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : done())}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {rows.length} {rows.length === 1 ? "candidato já" : "candidatos já"} no projeto
          </DialogTitle>
          <DialogDescription>
            Estes candidatos já estavam no projeto, então não foram duplicados. Arraste-os entre as
            listas para reposicioná-los — cada movimento é salvo na hora.
          </DialogDescription>
        </DialogHeader>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="grid flex-1 gap-3 overflow-y-auto sm:grid-cols-3">
            {STAGES.map(({ stage, title }) => (
              <Column
                key={stage}
                stage={stage}
                title={title}
                rows={rows.filter((r) => placement[r.id] === stage)}
              />
            ))}
          </div>
        </DndContext>

        <DialogFooter>
          <Button onClick={done}>Concluir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Column({
  stage,
  title,
  rows,
}: {
  stage: ProjectStage;
  title: string;
  rows: SourcingProjectCandidateView[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex min-h-[8rem] flex-col gap-2 rounded-lg border border-border bg-muted/20 p-2 transition-colors",
        isOver && "border-ring bg-accent/40",
      )}
    >
      <div className="flex items-center justify-between px-1">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {title}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {rows.length}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-1 py-3 text-center text-[11px] text-muted-foreground">Solte aqui</p>
      ) : (
        rows.map((r) => <Card key={r.id} row={r} />)
      )}
    </div>
  );
}

function Card({ row }: { row: SourcingProjectCandidateView }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: row.id });
  const c = row.candidate;
  const subtitle = [c.currentTitle, shortLocation(c.location)].filter(Boolean).join(" · ");
  return (
    <div
      ref={setNodeRef}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined}
      className={cn(
        "flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5",
        isDragging && "opacity-60 shadow-lg",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Arrastar candidato"
        className="shrink-0 cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical size={14} />
      </button>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium">
          {c.name || "Perfil sem nome"}
        </span>
        {subtitle && (
          <span className="block truncate text-[11px] text-muted-foreground">{subtitle}</span>
        )}
      </span>
    </div>
  );
}
